/**
 * ────────────────────────────────────────────────────────────────────────────
 *  LOCAL API OVERRIDE — the ONLY file you edit to point the UI at local backends
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Normally every XHR goes to the per-service base URL from `environment.local.apiUrls`
 *  (dev/prod gateways). This config lets you redirect any individual microservice to a
 *  locally-running instance WITHOUT touching `environment*.ts`, the endpoint constants,
 *  or any service file. With `nx serve` running, just edit this file and save — the app
 *  hot-reloads with the new routing.
 *
 *  HOW IT WORKS
 *  ------------
 *  Base URLs are chosen per request by `ServiceType` inside `HttpService` (DEFAULT, AUTH,
 *  PUBLIC, AI_SERVICE). `resolveLocalBaseUrl()` (used there) does:
 *    • `enabled: false` → nothing changes; the app behaves exactly as before (dev/prod).
 *    • `enabled: true`  → for each service, if its entry has `local: true`, requests for
 *                         that service go to its `baseUrl` (localhost); otherwise they
 *                         keep going to the normal dev/prod base URL.
 *  So you can run, say, product-service + public-api locally while auth + ai still hit dev.
 *
 *  USAGE
 *  -----
 *    1. set `enabled: true`
 *    2. flip `local: true` for each service you have running locally
 *    3. save — done. (Set `enabled: false` to return to pure dev/prod.)
 *
 *  LOCAL PORTS (defaults below)
 *    default (product-service web) : http://localhost:8181
 *    public  (public-api)          : http://localhost:8182
 *    auth    (auth-service)         : http://localhost:8084
 *    ai_service (ai-service)        : http://localhost:8081   (endpoint paths already include /api)
 *
 *  NOT COVERED (read their env vars directly, not via HttpService):
 *    • notification + forms/Instagram WebSockets (environment.local.apiUrls.notificationApiUrl /
 *      formsInstagramApiUrl) — change those in the env file if you need them local.
 *    • review-manager `/rw/...` rides on the `default` host, so flipping `default` covers it too.
 */

/** A leading-path rewrite applied only for local calls (gateway prefix → local controller path). */
export interface LocalPathRewrite {
  /** Gateway prefix as it appears in the endpoint constants, e.g. '/product-svc-ext/api'. */
  from: string;
  /** Local controller prefix to replace it with, e.g. '/api'. */
  to: string;
}

/** Per-service local override entry. */
export interface LocalServiceOverride {
  /** Flip to true to send this service's calls to {@link baseUrl}; false = use dev/prod. */
  local: boolean;
  /** Local origin used when `local` is true, e.g. 'http://localhost:8181'. */
  baseUrl: string;
  /**
   * Optional leading-prefix rewrites applied ONLY when this service is routed local. The dev gateway
   * strips its routing prefix (e.g. `/product-svc-ext`) before forwarding to the service; locally there
   * is no gateway, so we strip/rewrite it here. First matching rule wins — list most-specific first.
   */
  rewrites?: LocalPathRewrite[];
}

/**
 * Local API routing config. Keys MUST match the {@code ServiceType} enum string values
 * ('default' | 'public' | 'auth' | 'ai_service').
 */
export const LOCAL_API: {
  enabled: boolean;
  services: Record<string, LocalServiceOverride>;
} = {
  // ── MASTER SWITCH ── set true to activate the per-service routing below.
  enabled: false,

  services: {
    // product-service (web). Gateway prefixes → local /api controller path.
    default: {
      local: true,
      baseUrl: 'http://localhost:8181',
      rewrites: [
        { from: '/product-svc-ext/api', to: '/api' },
        { from: '/product-svc/api', to: '/api' },
        { from: '/product-v1/api', to: '/api' },
      ],
    },
    // public-api (onboarding, public form/chatbot endpoints). Gateway prefixes → /api/public/* controllers.
    public: {
      local: true,
      baseUrl: 'http://localhost:8182',
      rewrites: [
        { from: '/publicv2', to: '/api/public/v2' },
        { from: '/publicv1', to: '/api/public/v1' },
        { from: '/public', to: '/api/public' },
      ],
    },
    // auth-service (add rewrites here if local auth controller paths differ from the gateway prefix)
    auth: { local: false, baseUrl: 'http://localhost:8084' },
    // ai-service (paths already start with /api, matching the ai context-path — no rewrite needed)
    ai_service: { local: false, baseUrl: 'http://localhost:8081' },
  },
};

/**
 * Returns the base URL to use for a given service type, honoring {@link LOCAL_API}.
 * Falls back to the normal dev/prod `fallbackUrl` whenever local routing is disabled
 * or that service is not flipped to local.
 *
 * @param serviceType the ServiceType string value ('default' | 'public' | 'auth' | 'ai_service')
 * @param fallbackUrl the normal dev/prod base URL for that service
 */
export function resolveLocalBaseUrl(
  serviceType: string,
  fallbackUrl: string
): string {
  if (!LOCAL_API.enabled) {
    return fallbackUrl;
  }
  const svc = LOCAL_API.services[serviceType];
  if (svc && svc.local && svc.baseUrl) {
    return svc.baseUrl.replace(/\/+$/, '');
  }
  return fallbackUrl;
}

/**
 * Rewrites a request path's leading gateway prefix to the local controller path when that service is
 * routed local (e.g. '/product-svc-ext/api/notifications-events/count' → '/api/notifications-events/count').
 * Returns the path unchanged when local routing is off, the service isn't local, or no rule matches.
 *
 * @param serviceType the ServiceType string value ('default' | 'public' | 'auth' | 'ai_service')
 * @param path the relative request path (as built from the endpoint constants)
 */
export function resolveLocalPath(serviceType: string, path: string): string {
  if (!LOCAL_API.enabled) {
    return path;
  }
  const svc = LOCAL_API.services[serviceType];
  if (!svc || !svc.local || !svc.rewrites) {
    return path;
  }
  for (const rule of svc.rewrites) {
    if (path.startsWith(rule.from)) {
      return rule.to + path.slice(rule.from.length);
    }
  }
  return path;
}
